/**
 * Release notes in brief (1.0.0-rc.13): what the version window and the PDF show of each release. A release's
 * `summary` when it has one; until then the first features, cut to the summary's length, so the window and the PDF
 * work with or without the short versions.
 */
import { SUMMARY_MAX_WORDS, type Release } from '@prism/contracts';

/** How many features stand in for a missing summary. */
export const FALLBACK_SUMMARY_BULLETS = 3;

/** A bullet cut to at most `words` words, with an ellipsis when it was cut. */
export function truncateWords(text: string, words = SUMMARY_MAX_WORDS): string {
  const parts = text.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= words) return parts.join(' ');
  return `${parts.slice(0, words).join(' ').replace(/[,;:.–-]+$/, '')}…`;
}

/** The release in brief: its summary, or the first features (the first fixes when it has none) cut short. */
export function releaseSummary(r: Release): string[] {
  if (r.summary && r.summary.length > 0) return r.summary;
  const source = r.features.length > 0 ? r.features : r.fixes.map((f) => `Fixed: ${f}`);
  return source.slice(0, FALLBACK_SUMMARY_BULLETS).map((b) => truncateWords(b));
}

/** True when the summary is the stand-in made from the features. */
export function isFallbackSummary(r: Release): boolean {
  return !r.summary || r.summary.length === 0;
}

/** The section of docs/deployment.md with the steps for this release, as the app names docs elsewhere. */
export function upgradeGuideSection(r: Release): { file: string; section: string; anchor: string } | null {
  if (!r.upgradeGuide) return null;
  return { file: 'docs/deployment.md', section: `Upgrading to ${r.version}`, anchor: r.upgradeGuide };
}

/** Everything of a release that the search box looks through; the upgrade notes only for administrators. */
export function releaseSearchText(r: Release, admin: boolean): string {
  const parts = [r.version, r.title, r.date, ...releaseSummary(r), ...r.features, ...r.fixes];
  if (admin) parts.push(...(r.upgradeSummary ?? []), ...(r.beforeUpgrade ?? []));
  return parts.join('\n').toLowerCase();
}

/** Whether the search words (all of them, in any order) are in the release. */
export function releaseMatches(r: Release, query: string, admin: boolean): boolean {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const text = releaseSearchText(r, admin);
  return words.every((w) => text.includes(w));
}

/** Whether the search words are all in the release's brief part (so its details need not open to show the match). */
export function matchesInBrief(r: Release, query: string, admin: boolean): boolean {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const brief = [r.version, r.title, r.date, ...releaseSummary(r), ...(admin ? r.upgradeSummary ?? [] : [])].join('\n').toLowerCase();
  return words.every((w) => brief.includes(w));
}
